import { resolveActiveAuditOverrides, stripOverrideVersionSuffix } from './truthAttestations';
/**
 * R-81 — client-side user audit orchestrator (read-only).
 */
import { useStore } from '../../store';
import type { SiteEntity } from '../../types/site';
import type { SiteRagReport } from '../../types/siteRag';
import type { SiteEvidenceDocument } from '../../types/siteEvidence';
import type { ReportTypeId } from '../../config/reportTypes';
import type {
  AuditCheckId,
  AuditSeverity,
  AuditTier,
  UserAuditAnchor,
  UserAuditFinding,
  UserAuditResult,
} from '../../types/userAudit';
import {
  auditReport,
  capacitySsotFromPhases,
  isReportBodyEmpty,
  reconcileAcrossReports,
  type ReportDoc,
  type SiteReportAuditResult,
  type SiteReportFinding,
  type Tier2eMention,
} from './siteReportAuditor';
import { reconcileSiteFacts } from './canonicalFactReconciler';
import { effectivePhases } from '../projectIntent';
import {
  buildEvidenceCacheFromSed,
  isPrecedenceWaived,
  valuesAgree,
  verifyProviderPrecedence,
  type PrecedenceViolation,
} from './providerPrecedenceVerifier';
import {
  applyInternalNumberGate,
  canonicalFactKeyFromInternal,
  toInternalCanonicalKey,
} from './internalNumberReconciler';
import { reconcileIntentVsSed } from './intentSedReconciler';
import { checkProviderCoverage } from '../providerCoverageGate';
import {
  checkSectionDepth,
  collapsedStubProse,
  extractSectionsFromReport,
  stubRepeatDetailWithCoDetectedFields,
} from '../sectionDepthGate';
import { evaluateCitationDensityGate } from './citationDensityGate';
import { loadCitationDensityThreshold } from '../../services/citationDensityThreshold';
import type { CanonicalFactKey, ReconciliationFinding } from '../../types/auditCanonicalFacts';
import { AUDIT_CONFIG } from '../../config/auditConfig';
import { auditQualitativeClaims } from './qualitativeClaimAuditor';
import { auditSectionCoherence } from './sectionCoherenceAuditor';
import { verifyCitationSupport } from './citationSemanticVerifier';
import { mergeAllScenarios } from '../financialScenarios';
import { projectCompositeUnderScenario } from '../portfolio/scenarioCompositeProjection';
import { isUnavailableFloodZone, unavailableFloodZoneAction } from './floodZoneAvailability';

export const AUDIT_DEBOUNCE_MS = 30_000;

function hashStable(input: string): string {
  let h = 0;
  for (let i = 0; i < input.length; i++) {
    h = ((h << 5) - h + input.charCodeAt(i)) | 0;
  }
  return Math.abs(h).toString(36);
}

function parseTierLabel(raw: string): { tier: AuditTier; checkId: AuditCheckId } {
  if (raw.startsWith('Tier 0')) return { tier: 'Tier 0', checkId: 'R-65' };
  if (raw.startsWith('Tier 1')) return { tier: 'Tier 1', checkId: 'tier-1' };
  if (raw.includes('Tier 2b')) return { tier: 'Tier 2', checkId: 'tier-2b' };
  if (raw.includes('Tier 2d')) return { tier: 'Tier 2', checkId: 'tier-2d' };
  if (raw.includes('Tier 2e')) return { tier: 'Tier 2', checkId: 'tier-2e' };
  if (raw.includes('Tier 2f')) return { tier: 'Tier 2', checkId: 'tier-2f' };
  if (raw.includes('Tier 2k')) return { tier: 'Tier 2', checkId: 'tier-2k' };
  if (raw.includes('Tier 2l')) return { tier: 'Tier 2', checkId: 'tier-2l' };
  if (raw.includes('Tier 2m')) return { tier: 'Tier 2', checkId: 'tier-2m' };
  if (raw.includes('Tier 2n')) return { tier: 'Tier 2', checkId: 'tier-2n' };
  if (raw.startsWith('Tier 3')) return { tier: 'Tier 3', checkId: 'tier-3' };
  if (raw.startsWith('Tier 4')) return { tier: 'Tier 4', checkId: 'tier-4' };
  return { tier: 'Tier 2', checkId: 'tier-2b' };
}

function makeFindingId(
  checkId: AuditCheckId,
  reportId: string | undefined,
  message: string,
): string {
  return `${checkId}:${reportId ?? 'site'}:${hashStable(message)}`;
}

/**
 * Regeneration-stable identity for a finding: unlike `makeFindingId` it omits
 * the report id, so a validate override recorded against it keeps clearing the
 * SAME condition (same check, same report type, same message) on the report's
 * next revision. A regeneration that CHANGES the finding (different values →
 * different message) produces a different key and resurfaces for review.
 */
export function makeStableFindingKey(
  checkId: string,
  reportType: string | undefined,
  message: string,
): string {
  return `${checkId}:${reportType ?? 'any'}:${hashStable(message)}`;
}

/**
 * U6 — kind of actionable control the FINDINGS table should render for a check.
 *  - `reconcile`   → internal-number inconsistency; show a value-picker popover.
 *  - `navigate`    → deep-link (report editor, doc viewer, financial tab).
 *  - `view-detail` → no remediation route; surface the existing expandable detail.
 */
export type DefaultActionKind = 'reconcile' | 'navigate' | 'view-detail';

export interface DefaultAction {
  /** Short ACTION-column label. Always populated so the column is never empty. */
  label: string;
  kind: DefaultActionKind;
  /** Navigation target for `navigate`-kind actions. */
  href?: string;
  /**
   * For `reconcile`-kind actions: the distinct values the user may promote to
   * canonical. Parsed from the tier-2e / R-67 finding message
   * ("…cited with N distinct values …: 1500, 1750, 750").
   */
  candidateValues?: string[];
}

/** Internal-number inconsistency checks that should offer a Reconcile control. */
function isReconcilableCheck(checkId: AuditCheckId): boolean {
  return checkId === 'R-67' || checkId === 'tier-2e';
}

/**
 * Parse the distinct values out of an internal-number inconsistency message,
 * e.g. "Capacity (MW) cited with 3 distinct values in same report: 1500, 1750, 750".
 * Returns undefined when no value list can be recovered.
 */
function parseCandidateValues(message: string, context?: string): string[] | undefined {
  const colonIdx = message.lastIndexOf(':');
  if (colonIdx >= 0) {
    const tail = message.slice(colonIdx + 1).trim();
    const parts = tail
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean);
    if (parts.length >= 2) return parts;
  }
  // Fall back to the per-mention context block ("  - 1500 → \"…\"").
  if (context) {
    const seen = new Set<string>();
    for (const line of context.split('\n')) {
      const m = line.match(/^\s*-\s*([^→\n]+?)\s*(?:→|$)/);
      if (m && m[1]) seen.add(m[1].trim());
    }
    if (seen.size >= 2) return [...seen];
  }
  return undefined;
}

/**
 * Default remediation action for a check. Returns an ACTION label, the control
 * `kind` the FINDINGS table should render, and (where applicable) a deep-link
 * href or the candidate values for an internal-number reconcile popover, so the
 * ACTION column is always actionable rather than showing "—".
 *
 * - Internal-number inconsistency (tier-2e / R-67) → `reconcile` + candidateValues.
 * - Tier-* section / R-73 section-depth → `navigate` to the report editor.
 * - Doc / citation provenance (R-70) → `navigate` to the source doc.
 * - Anything else with only an expandable detail → `view-detail`.
 */
export function defaultActionForCheck(
  checkId: AuditCheckId,
  tier: AuditTier,
  ctx?: {
    siteId?: string;
    reportId?: string;
    message?: string;
    context?: string;
    docId?: string;
  },
): DefaultAction {
  const reportHref =
    ctx?.siteId && ctx?.reportId
      ? `/sites/${encodeURIComponent(ctx.siteId)}/reports/${encodeURIComponent(ctx.reportId)}`
      : undefined;

  // (1) Internal-number inconsistency → Reconcile value-picker.
  if (isReconcilableCheck(checkId)) {
    const candidateValues = ctx?.message
      ? parseCandidateValues(ctx.message, ctx.context)
      : undefined;
    if (candidateValues && candidateValues.length >= 2) {
      return { label: 'Reconcile…', kind: 'reconcile', candidateValues, href: reportHref };
    }
    // No recoverable values — fall through to a navigate/view-detail control.
  }

  // (3) Doc / citation provenance → Open source (R-70 surfaces a docId).
  if ((checkId as string) === 'R-70' && ctx?.docId) {
    return { label: 'Open source', kind: 'navigate', href: `/docs/${encodeURIComponent(ctx.docId)}` };
  }

  // (2) Section depth.
  if (checkId === 'R-73') {
    return reportHref
      ? { label: 'Expand section to meet depth floor', kind: 'navigate', href: reportHref }
      : { label: 'Expand section to meet depth floor', kind: 'view-detail' };
  }

  // (2) Tier-* checks (and any unmapped check) → regenerate the affected section.
  return reportHref
    ? { label: 'Regenerate affected section', kind: 'navigate', href: reportHref }
    : { label: 'Regenerate affected section', kind: 'view-detail' };
}

/** P3 — clamp an excerpt to a short, highlight-friendly snippet. */
const ANCHOR_EXCERPT_MAX = 160;
function trimExcerpt(raw: string | undefined): string | undefined {
  if (!raw) return undefined;
  const cleaned = raw.replace(/\s+/g, ' ').trim();
  if (!cleaned) return undefined;
  return cleaned.length > ANCHOR_EXCERPT_MAX
    ? `${cleaned.slice(0, ANCHOR_EXCERPT_MAX - 1)}…`
    : cleaned;
}

/**
 * P3 — build a finding anchor from the auditor's structured mentions.
 *
 * Picks the MOST-RELEVANT mention: the first mention that carries a
 * `sectionId` (the primary scroll target), else the first mention overall.
 * Maps its sectionId + lineNumber + a trimmed excerpt onto the anchor.
 * Returns undefined when there are no mentions or no usable locator —
 * the finding then carries no `anchor` (unchanged, pre-P3 behavior).
 */
function anchorFromMentions(
  mentions: Tier2eMention[] | undefined,
): UserAuditAnchor | undefined {
  if (!mentions || mentions.length === 0) return undefined;
  const best = mentions.find((m) => m.sectionId) ?? mentions[0]!;
  const excerpt = trimExcerpt(best.excerpt);
  const anchor: UserAuditAnchor = {
    ...(best.sectionId ? { sectionId: best.sectionId } : {}),
    ...(typeof best.lineNumber === 'number' ? { lineNumber: best.lineNumber } : {}),
    ...(excerpt ? { excerpt } : {}),
  };
  // Only return an anchor that actually points somewhere.
  return anchor.sectionId || anchor.lineNumber != null || anchor.excerpt
    ? anchor
    : undefined;
}

function mapSiteReportFinding(
  f: SiteReportFinding,
  siteId?: string,
  reportId?: string,
  reportType?: string,
): UserAuditFinding {
  const { tier, checkId } = parseTierLabel(f.tier);
  const { label, href } = defaultActionForCheck(checkId, tier, {
    siteId,
    reportId,
    message: f.message,
    context: f.context,
  });
  // P3 — propagate the paragraph anchor from the auditor's structured mentions
  // (Tier-2e internal-number findings carry these). Findings with no mention
  // get no anchor and behave exactly as before.
  const anchor = anchorFromMentions(f.mentions);
  return {
    id: makeFindingId(checkId, reportId, f.message),
    severity: f.severity,
    tier,
    checkId,
    message: f.message,
    // D — `detail` used to duplicate `context`, so the expanded card rendered
    // the same mention list twice. Context is the one provenance payload.
    context: f.context,
    reportId,
    reportType,
    action: label,
    ...(href ? { actionHref: href } : {}),
    ...(anchor ? { anchor } : {}),
    ...(f.canonicalFactKey ? { canonicalFactKey: canonicalFactKeyFromInternal(f.canonicalFactKey) ?? undefined } : {}),
    // E — Tier-3 structured picker candidates (truth-first), typed data.
    ...(f.reconcileCandidates && f.reconcileCandidates.length >= 2
      ? { reconcileCandidates: f.reconcileCandidates }
      : {}),
  };
}

export function dedupeUserAuditFindings(findings: UserAuditFinding[]): UserAuditFinding[] {
  const seen = new Set<string>();
  const out: UserAuditFinding[] = [];
  for (const f of findings) {
    if (seen.has(f.id)) continue;
    seen.add(f.id);
    out.push(f);
  }
  return out;
}

export function aggregateSeverityCounts(
  findings: UserAuditFinding[],
): Record<AuditSeverity, number> {
  const counts: Record<AuditSeverity, number> = { block: 0, warn: 0, info: 0 };
  // Validated/dismissed findings stay in the result (visible + undoable) but
  // are settled/parked decisions — they must not count against severity or gates.
  for (const f of findings) {
    if (f.validated || f.dismissed) continue;
    counts[f.severity] += 1;
  }
  return counts;
}

/**
 * P3 — anchor for a cross-report reconciliation finding (R-79). No section id
 * is available at this layer, so we derive the locator from a per-report
 * `CanonicalFactValue`: prefer the truth report's value (the value the user
 * should align dissenters to), else the first report value carrying an
 * excerpt. `citationRange.startLine` supplies the lineNumber. Returns
 * undefined when no report value carries an excerpt or line.
 */
function anchorFromReconciliation(f: ReconciliationFinding): UserAuditAnchor | undefined {
  const entries = Object.entries(f.reportValues);
  if (entries.length === 0) return undefined;
  const truthReportId = f.truthValue?.source;
  const preferred =
    (truthReportId ? f.reportValues[truthReportId] : undefined)
    ?? entries.map(([, v]) => v).find((v) => v.excerpt || v.citationRange)
    ?? entries[0]![1];
  const excerpt = trimExcerpt(preferred.excerpt);
  const lineNumber = preferred.citationRange?.startLine;
  const anchor: UserAuditAnchor = {
    ...(typeof lineNumber === 'number' ? { lineNumber } : {}),
    ...(excerpt ? { excerpt } : {}),
  };
  return anchor.lineNumber != null || anchor.excerpt ? anchor : undefined;
}

function mapReconciliationFinding(f: ReconciliationFinding): UserAuditFinding {
  const anchor = anchorFromReconciliation(f);
  // Structured reconcile candidates: source-of-truth value first, then each
  // distinct report value. Typed data, not message parsing — this is what
  // makes the Reconcile picker available for cross-report (R-79) divergence,
  // including text-valued facts like grid operator.
  const candidateValues = [
    ...(f.truthValue ? [String(f.truthValue.value)] : []),
    ...Object.values(f.reportValues).map((v) => String(v.value)),
  ];
  const reconcileCandidates = candidateValues.filter((v, i) => v !== '' && candidateValues.indexOf(v) === i);
  return {
    id: makeFindingId('R-79', undefined, `${f.factKey}:${f.message}`),
    severity: f.severity,
    tier: 'Tier 3',
    checkId: 'R-79',
    message: f.message,
    detail: f.remediationHint,
    canonicalFactKey: f.factKey,
    ...(reconcileCandidates.length >= 2 ? { reconcileCandidates } : {}),
    action: f.remediationHint,
    ...(anchor ? { anchor } : {}),
  };
}

function mapPrecedenceViolation(
  v: PrecedenceViolation,
  reportId: string,
  reportType: string,
): UserAuditFinding {
  const unavailableFlood = v.fact === 'floodZone' && isUnavailableFloodZone(v.tier1Value);
  const violation: PrecedenceViolation = unavailableFlood ? {
    ...v, kind: 'provider-unavailable', severity: 'warn',
    suggestedFix: unavailableFloodZoneAction(v.reportedValue, v.tier1Source),
  } : v;
  // P3 — precedence violations carry no sectionId/lineNumber, but the offending
  // `excerpt` lets the panel match/highlight the passage in the rendered report.
  const excerpt = trimExcerpt(v.excerpt);
  const anchor: UserAuditAnchor | undefined = excerpt ? { excerpt } : undefined;
  return {
    id: makeFindingId('R-78', reportId, `${v.fact}:${v.reportedValue}`),
    severity: violation.severity,
    tier: 'Tier 2',
    checkId: 'R-78',
    message: unavailableFlood ? `${v.tier1Source} flood zone is unavailable; verify the report's zone ${v.reportedValue} against its cited source`
      : `${v.fact}: report=${v.reportedValue}, tier1=${v.tier1Value} (${v.tier1Source})`,
    detail: violation.suggestedFix,
    reportId,
    reportType,
    providerSource: v.tier1Source,
    precedenceViolation: violation,
    action: violation.suggestedFix,
    context: v.excerpt,
    ...(anchor ? { anchor } : {}),
  };
}

function buildScenarioSensitivityFinding(
  site: SiteEntity,
  reports: SiteRagReport[],
): UserAuditFinding | null {
  const fa = site.financialAssumptions;
  if (!fa || (fa.scenarios?.length ?? 0) === 0) return null;

  let projections: Array<{
    id: string;
    label: string;
    band: string;
    isCanonical: boolean;
    crossing: boolean;
  }> = [];
  try {
    const merged = mergeAllScenarios(fa);
    projections = merged.map((scenario) => {
      const projected = projectCompositeUnderScenario(site, reports, scenario);
      return {
        id: projected.scenarioId,
        label: projected.scenarioLabel,
        band: projected.projectedBandLabel,
        isCanonical: scenario.scenario.isCanonical,
        crossing: projected.bandCrossing,
      };
    });
  } catch {
    // Fire-and-forget: never fail audit if projection path is incomplete.
    return null;
  }

  const nonCanonical = projections.filter((p) => !p.isCanonical);
  const crossings = nonCanonical.filter((p) => p.crossing);

  // A check that PASSES is not a finding (2026-08-13, William): "all modeled
  // scenarios remain in-band (Upside → CONDITIONAL; Downside → CONDITIONAL)"
  // read as something to fix, in jargon, when it means the decision is ROBUST
  // — nothing changes under any scenario. Robustness lives on the Scenario
  // compare page; the ledger only carries the fragile case.
  if (crossings.length === 0) return null;

  const severity: AuditSeverity = crossings.length >= 2 ? 'block' : 'warn';
  const canonicalBand = projections.find((p) => p.isCanonical)?.band;
  // Plain language: name each scenario that flips the decision and what it
  // flips to — "Downside → CONDITIONAL" arrow-speak read as a transition
  // diagram, not a sentence.
  const flips = crossings
    .map((p) => `under the ${p.label} scenario the decision would become ${p.band}`)
    .join('; ');

  return {
    id: makeFindingId('R-FA-SCEN', undefined, flips),
    severity,
    tier: 'Tier 2',
    checkId: 'R-FA-SCEN',
    message: `The site decision is sensitive to financial assumptions: ${flips}${
      canonicalBand ? ` (current decision: ${canonicalBand})` : ''
    }. Review the scenario assumptions before relying on the current verdict.`,
    detail: 'At least one modeled financial scenario moves the site across a decision-band boundary — the verdict depends on which assumptions hold.',
    action: 'Open scenario compare',
    actionHref: `/sites/${encodeURIComponent(site.id)}/financial?tab=scenarios`,
  };
}

async function loadSedForSite(site: SiteEntity): Promise<SiteEvidenceDocument | null> {
  if (!site.sedId) return null;
  const { loadSedById } = await import('../../services/sedLoader');
  // sedVersion keys the loader cache — an in-place SED rebuild keeps the same
  // id/path but bumps the version, so omitting it returned the PRIOR cached
  // SED despite the fresh server read (Codex review on #131).
  return loadSedById(site.sedId, site.sedStoragePath, site.sedVersion);
}

function runTierAudits(
  site: SiteEntity,
  reports: SiteRagReport[],
): { reportAudits: ReturnType<typeof auditReport>[]; crossReportFindings: SiteReportFinding[] } {
  const reportDocs = reports as unknown as ReportDoc[];
  // S1 — derive the phase-aware capacity SSOT ONCE and thread it into the
  // per-report Tier-2e detector so a per-phase cumulative MW (e.g. 276 at
  // Phase-1) and the ultimate build-out (1000) no longer false-block as a
  // within-report "conflict".
  const phasesView = site.projectIntent ? effectivePhases(site.projectIntent) : null;
  const capacitySsot = capacitySsotFromPhases(phasesView, {
    requirementBucket: site.projectIntent?.powerRequirement,
  });
  // Recorded reconcile decisions, keyed under BOTH the public and internal
  // canonical-key spellings — Tier-2e reads the internal key. A settled fact
  // demotes its per-report conflict to info instead of re-blocking a decision
  // the user already made (the R-67 gate consumes the same overrides at
  // regeneration time; this detector ran override-blind in parallel).
  const reconcileOverrides = new Map<string, string | number>();
  for (const o of resolveActiveAuditOverrides(site.auditOverrides)) {
    if ('type' in o && o.type === 'reconcile' && o.canonicalKey) {
      reconcileOverrides.set(o.canonicalKey, o.canonicalValue);
      const internal = toInternalCanonicalKey(o.canonicalKey as CanonicalFactKey);
      if (internal) reconcileOverrides.set(internal, o.canonicalValue);
    }
  }
  const reportAudits = reportDocs.map((r) => auditReport(r, { capacitySsot, reconcileOverrides }));
  const siteAudit: SiteReportAuditResult = {
    siteId: site.id,
    intent: site.projectIntent
      ? {
          powerRequirementMW: site.projectIntent.powerRequirementMW,
          // Phased sites: the ultimate (final-phase cumulative) capacity is
          // the canonical-fact truth — phased intents often leave
          // powerRequirementMW null. effectivePhases() returns final/max.
          totalCapacityMW: capacitySsot.ultimateMW,
          coolingStrategy: site.projectIntent.coolingStrategy,
          workloadProfile: site.projectIntent.workloadProfile,
        }
      : undefined,
    reports: reportAudits,
    crossReportFindings: [],
  };
  // A — the cross-report reconciler corroborates against the SAME phase SSOT
  // and reconcile overrides as Tier-2e; it ran phase-blind until 2026-08-12.
  reconcileAcrossReports(siteAudit, reportDocs, { capacitySsot, reconcileOverrides });
  return { reportAudits, crossReportFindings: siteAudit.crossReportFindings };
}

function runProviderCoverageChecks(
  reports: SiteRagReport[],
  sed: SiteEvidenceDocument | null,
): Map<ReportTypeId, ReturnType<typeof checkProviderCoverage>> {
  const byType = new Map<ReportTypeId, ReturnType<typeof checkProviderCoverage>>();
  // Adversarial-review fix (2026-08-13): with NO SED at all, every provider
  // of every report type resolves 'missing' with the same "No Site Evidence
  // Document loaded." message — a single absent INPUT rendered as 19+
  // blocking data findings (live: a transient SED load failure zeroed the
  // trust score). The caller emits ONE finding for that state instead; the
  // per-provider expansion only runs against an actual document.
  if (!sed) return byType;
  for (const r of reports) {
    const rt = r.reportType as ReportTypeId;
    if (!rt || byType.has(rt)) continue;
    byType.set(rt, checkProviderCoverage(rt, sed));
  }
  return byType;
}

function runPrecedenceChecks(
  reports: SiteRagReport[],
  sed: SiteEvidenceDocument | null,
  intent?: import('../../types/siteRag').ProjectIntent | null,
  auditOverrides?: import('../../types/site').SiteAuditOverride[],
): Record<string, PrecedenceViolation[]> {
  const evidence = buildEvidenceCacheFromSed(sed, { intent });

  // PROVIDER-PRECEDENCE WAIVERS (2026-08-14): the user's recorded "keep the
  // KMZ/developer value" decisions (legacy {gate:'provider-precedence',
  // fields:[factKey]} overrides — Redhead has FOUR for
  // interconnectionDistanceKm from June) must suppress R-78 for that fact.
  // The fresh recompute ignored them and resurrected a settled decision the
  // moment the SED re-resolved a different provider value (v35: GILLIS @
  // 5.2 km vs the waived KMZ 2.9 km). A waiver scoped to a reportTypeId
  // applies to that type; an unscoped one applies site-wide.
  const waiverFor = (reportType: string | undefined, fact: string): boolean =>
    isPrecedenceWaived(auditOverrides, reportType, fact);
  // Stamped violations are FROZEN at generation time — re-check each against
  // the CURRENT acceptance rules (same doctrine as reevaluateStampedHoldFindings,
  // PR #127): a value the widened tier-1 set now corroborates (e.g. the 69 kV
  // side of an intent-declared 230/69 kV substation) is no longer a violation.
  // A stored violation whose fact has no current tier-1 value is KEPT — there
  // is no basis to clear it.
  const survivesReeval = (v: { fact: string; reportedValue: string | number }): boolean => {
    const tier1 = evidence.providerData[v.fact as PrecedenceViolation['fact']];
    if (!tier1) return true;
    const accepted: (string | number)[] = [tier1.value, ...(tier1.altValues ?? [])];
    return !accepted.some((a) => valuesAgree(v.fact as PrecedenceViolation['fact'], v.reportedValue, a));
  };
  const out: Record<string, PrecedenceViolation[]> = {};
  for (const r of reports) {
    // ZOMBIE-STAMP FIX (2026-08-13, Redhead "is 2" RCA): when the report
    // BODY is available, recompute R-78 from the live text — the audit runs
    // AFTER shell hydration (J'), so a body is normally present. Stamps are
    // frozen at generation time and, worse, an update-mode draft INHERITS
    // its parent's stamp — a false positive from a since-fixed extractor
    // ("is 2" from 'substation is 2.9 km') re-blocked every regeneration
    // even though the fresh body yields ZERO violations. The stored stamp
    // is only trusted for bodyless shells, and even then re-checked against
    // current acceptance rules (survivesReeval).
    const stored = r.qaFindings?.providerPrecedenceViolations;
    const bodyAvailable = !isReportBodyEmpty(r as unknown as ReportDoc);
    const violations: PrecedenceViolation[] = (bodyAvailable || !stored?.length
      ? verifyProviderPrecedence(r, evidence)
      : stored
          .filter(survivesReeval)
          .map((v) => ({
            fact: v.fact as PrecedenceViolation['fact'],
            reportedValue: v.reportedValue,
            tier1Value: v.tier1Value,
            tier1Source: v.tier1Source as PrecedenceViolation['tier1Source'],
            severity: v.severity,
            suggestedFix: v.suggestedFix,
            excerpt: v.excerpt,
          }))
    ).filter((v) => !waiverFor(r.reportType, v.fact));
    if (violations.length > 0) out[r.id] = violations;
  }
  return out;
}

function runSectionDepthChecks(
  reports: SiteRagReport[],
): Array<{
  report: SiteRagReport;
  /** The extraction the result was computed from — the overlap test re-reads it. */
  sections: Array<{ id: string; content: string }>;
  result: ReturnType<typeof checkSectionDepth>;
}> {
  return (
    reports
      // J — a body-less report already gets its single "body unavailable"
      // block from auditReport; per-section depth findings on a shell are
      // 8+ echoes of that one cause.
      .filter((r) => !isReportBodyEmpty(r as unknown as ReportDoc))
      .map((r) => {
        const sections = extractSectionsFromReport(r);
        return {
          report: r,
          sections,
          result: checkSectionDepth(
            sections,
            r.reportType as ReportTypeId,
            // Explicitly-omitted sections (unevidenced domains) demote to warn —
            // "expand to meet depth floor" is unpassable when no facts exist.
            new Set((r.qaFindings?.sectionOmissions ?? []).map((o) => o.sectionId)),
            // Uniform-stub collapse attribution — one provider outage reads as
            // ONE warn + rerun recommendation, not N per-section blocks.
            {
              degradedMode: r.degradedMode,
              narrativeFailureKind: r.generationTelemetry?.narrativeFailureKind,
            },
          ),
        };
      })
  );
}

/** READ-ONLY — runs all automatable audit gates against in-memory store data. */
export async function runUserAudit(siteId: string): Promise<UserAuditResult> {
  const started = Date.now();
  const state = useStore.getState();
  let site = state.siteRag.sites.find((s) => s.id === siteId);
  if (!site) {
    throw new Error(`Site ${siteId} not found in store`);
  }

  // H' (2026-08-13, Redhead trust-0 RCA) — audit FRESH site truth, never a
  // cached snapshot. On a cold page load the Firestore client can serve the
  // sites array from its local cache first; the audit tab auto-runs
  // immediately and audited a months-old site version (an old 300 MW phase
  // plan, 1 of 10 recorded decisions) — every current report "contradicted"
  // a baseline that no longer existed and trust collapsed to 0 until a
  // manual re-run. Re-read the site doc from the SERVER and overlay the
  // audit-critical fields (decision trail, intent/phase SSOT inputs, SED
  // pointer). Offline or transient failure falls back to the store copy —
  // same behaviour as before this guard.
  try {
    const { auth, db } = await import('../../firebase');
    const user = auth.currentUser;
    if (user) {
      const { doc, getDocFromServer } = await import('firebase/firestore');
      const { siteOwnerSitesPath } = await import('../siteOwnerPath');
      const fresh = await getDocFromServer(
        doc(db, `${siteOwnerSitesPath(site, user.uid)}/${siteId}`),
      );
      const freshData = fresh.exists() ? fresh.data() : null;
      if (freshData) {
        // AUTHORITATIVE VERBATIM (Codex review on #131/#132): once the
        // server doc exists, its fields replace the cached copy INCLUDING
        // null/absent — `deleteSiteSed` writes sedId: null and a cleared
        // intent/decision trail is represented by omission; a `??` fallback
        // would resurrect exactly the stale truth this read exists to
        // prevent. The store copy stands in only when the read FAILS.
        // financialAssumptions + sedVersion ride along so scenario gates
        // and the version-keyed SED cache never see a hybrid site.
        site = {
          ...site,
          auditOverrides: freshData.auditOverrides,
          projectIntent: freshData.projectIntent,
          financialAssumptions: freshData.financialAssumptions,
          sedId: freshData.sedId,
          sedStoragePath: freshData.sedStoragePath,
          sedVersion: freshData.sedVersion,
        };
      }
    }
  } catch {
    // Store copy stands in when the server read fails (offline/dev).
  }

  // Re-read AFTER the server round-trip — the reports listener may have
  // delivered new snapshots during the await; pairing fresh site truth with
  // pre-await report metadata reproduces the very contradiction findings the
  // fresh read prevents (Codex review on #132).
  const allReports = useStore.getState().siteRag.reports.filter((r) => r.siteId === siteId);
  const byType = new Map<string, typeof allReports[0]>();
  for (const r of allReports) {
    const rType = r.reportType || 'unknown';
    const existing = byType.get(rType);
    if (!existing || new Date(r.createdAt).getTime() > new Date(existing.createdAt).getTime()) {
      byType.set(rType, r);
    }
  }
  let reports = Array.from(byType.values());

  // J' (2026-08-12, Redhead RCA) — audit REAL bodies, not lazy shells. Store
  // reports hydrate their GCS body only when opened in the reader; a report
  // the user never opened is `_metadataOnly` and auditing it produced a
  // 16-block "every section missing" flood while the stored body was fully
  // populated. Recover each shell before auditing; a report that STILL has
  // no body after this falls through to the single "body unavailable" block.
  const shells = reports.filter(
    (r) => (r as { _metadataOnly?: boolean })._metadataOnly === true,
  );
  if (shells.length > 0) {
    await Promise.all(shells.map((r) => state.hydrateReportBody(r.id)));
    const refreshed = useStore.getState().siteRag.reports;
    reports = reports.map((r) => refreshed.find((fresh) => fresh.id === r.id) ?? r);
  }

  const reportIds = reports.map((r) => r.id);
  const sed = await loadSedForSite(site);

  const [
    tierResult,
    canonicalReconciliation,
    intentSedReconciliation,
    providerCoverageResults,
    sectionDepthResults,
    precedenceByReport,
  ] = await Promise.all([
    Promise.resolve(runTierAudits(site, reports)),
    Promise.resolve(reconcileSiteFacts(site, reports, sed)),
    Promise.resolve(
      site.projectIntent && sed
        ? reconcileIntentVsSed(site.projectIntent, sed)
        : undefined,
    ),
    Promise.resolve(runProviderCoverageChecks(reports, sed)),
    Promise.resolve(runSectionDepthChecks(reports)),
    Promise.resolve(runPrecedenceChecks(reports, sed, site.projectIntent, site.auditOverrides)),
  ]);

  const findings: UserAuditFinding[] = [];
  const scenarioSensitivityFinding = buildScenarioSensitivityFinding(site, reports);
  if (scenarioSensitivityFinding) {
    findings.push(scenarioSensitivityFinding);
  }

  // Per report, the normalized PROSE R-73's uniform-stub collapse subsumed —
  // see the Tier-2k skip below.
  //
  // Match on CONTENT, never on section ids. The two stub detectors read
  // different id namespaces (siteReportAuditor.extractSections yields
  // keyFindings/risks/recommendations/insights/gridStudy that
  // extractSectionsFromReport never yields; R-73 resolves canonical ids
  // through field candidates and drops STUB_SKIP/omitted sections), and no
  // mapping between them is total in either direction — each attempt has been
  // wrong at some edge, either double-reporting one outage or deleting a real
  // second stub group. What both engines actually detect is "these sections
  // carry byte-identical prose", so the prose is the comparable half.
  const collapsedStubProseByReport = new Map<string, Set<string>>();
  for (const { report, sections, result } of sectionDepthResults) {
    const prose = collapsedStubProse(sections, result.failures);
    if (prose.size > 0) collapsedStubProseByReport.set(report.id, prose);
  }

  // Suppressing a Tier-2k twin must not take its SECTION NAMES down with it.
  // The auditor reads report fields R-73's extraction never yields, so one
  // outage stamping both namespaces left the survivor naming a strict subset
  // of what carries the stub. Collected here, per report and keyed by the
  // same prose the skip matches on, and folded into the R-73 message below.
  const coDetectedStubFieldsByReport = new Map<string, Map<string, string[]>>();

  for (const ra of tierResult.reportAudits) {
    for (const f of ra.findings) {
      // J — the R-73 gate (omission-aware, runs below for every report in
      // this path) is the section-depth authority here; Tier-2l is its older
      // twin inside auditReport and double-reported every thin/missing
      // section under slightly different wording. Tier-2l still guards the
      // entry points that do not run the R-73 gate (runFullAudit, scripts).
      if (f.tier.startsWith('Tier 2l')) continue;
      // Same doctrine for Tier-2k: when R-73's collapse has already reported
      // this repetition, Tier-2k mints a SECOND block over it — the very
      // per-section noise the collapse exists to remove, re-added one row
      // later. R-73's row wins because it is attribution-aware (one warn
      // while the provider failure stands) and omission-aware; Tier-2k still
      // guards the reports R-73 skips (body-less shells) and the entry points
      // that never run the gate.
      //
      // The test is per FINDING and it is a CONTENT identity: suppress only
      // when the exact prose this row reports is prose R-73 collapsed on this
      // report. A Tier-2k group carrying different prose is an independent
      // stub group and always survives, whatever section ids it names — and a
      // row with no recorded prose is never suppressed.
      if (f.tier.startsWith('Tier 2k') && f.stubProse) {
        if (collapsedStubProseByReport.get(ra.reportId)?.has(f.stubProse)) {
          const byProse = coDetectedStubFieldsByReport.get(ra.reportId) ?? new Map<string, string[]>();
          byProse.set(f.stubProse, [
            ...(byProse.get(f.stubProse) ?? []),
            ...(f.stubSectionIds ?? []),
          ]);
          coDetectedStubFieldsByReport.set(ra.reportId, byProse);
          continue;
        }
      }
      findings.push(mapSiteReportFinding(f, site.id, ra.reportId, ra.reportType));
    }
  }
  // CROSS-ENGINE DEDUPE (2026-08-13, Redhead flood-zone twin rows): the
  // auditor's Tier-3 sweep and the R-79 reconciler both flag the SAME
  // real-world disagreement under different fact labels ("FEMA 100-yr flood
  // zone" vs "FEMA flood zone") — one conflict rendered as TWO blocking
  // rows, double-charged against trust. When the reconciler carries the
  // equivalent fact, its row wins (per-report attribution + fix picker) and
  // the auditor's twin is dropped. Same one-defect-one-finding doctrine as
  // the M-series density/telemetry merge.
  const CROSS_ENGINE_FACT_TWINS: Record<string, string> = {
    'FEMA 100-yr flood zone': 'floodZone',
  };
  const reconcilerFactKeys = new Set(
    canonicalReconciliation.findings.map((f) => (f as { factKey?: string }).factKey).filter(Boolean),
  );
  for (const f of tierResult.crossReportFindings) {
    const twinKey = Object.entries(CROSS_ENGINE_FACT_TWINS)
      .find(([label]) => f.message.includes(label))?.[1];
    if (twinKey && reconcilerFactKeys.has(twinKey)) continue;
    findings.push(mapSiteReportFinding(f, site.id));
  }

  for (const f of canonicalReconciliation.findings) {
    findings.push(mapReconciliationFinding(f));
  }

  if (intentSedReconciliation) {
    for (const d of intentSedReconciliation.divergences) {
      findings.push({
        id: makeFindingId('R-65', undefined, `${d.field}:${d.intentValue}`),
        severity: d.severity,
        tier: 'Tier 0',
        checkId: 'R-65',
        message: `${d.field} diverges between Project Intent and SED`,
        detail: d.recommendedAction,
        intentValue: d.intentValue,
        sedValue: d.sedValue,
        sedExcerpt: d.sedExcerpt,
        action: d.recommendedAction,
        // The correction lives on the Intent tab — without a route this
        // finding family had no fix affordance anywhere.
        actionHref: `/sites/${site.id}?tab=intent`,
      });
    }
  }

  for (const [reportId, violations] of Object.entries(precedenceByReport)) {
    const report = reports.find((r) => r.id === reportId);
    for (const v of violations) {
      findings.push(mapPrecedenceViolation(v, reportId, report?.reportType ?? ''));
    }
  }

  // Adversarial-review fix (2026-08-13): one absent input → ONE finding.
  // Without a SED the provider sweep is skipped entirely (see
  // runProviderCoverageChecks); say so once, honestly, with the right next
  // step — never 19 identical "No Site Evidence Document loaded" blocks.
  if (!sed && reports.length > 0) {
    findings.push({
      id: makeFindingId('R-71', undefined, 'sed-not-loaded'),
      severity: 'block',
      tier: 'Tier 0',
      checkId: 'R-71',
      message: site.sedId
        ? 'Site evidence could not be loaded — provider coverage is unverifiable this run. Re-run the audit; if it persists, check storage credentials.'
        : 'No Site Evidence Document exists yet — run SED bootstrap, then re-run the audit to verify provider coverage.',
      action: site.sedId ? 'Re-run audit' : 'Run SED bootstrap',
    });
  }

  for (const [reportType, coverage] of providerCoverageResults) {
    for (const fail of coverage.blockingFailures) {
      findings.push({
        id: makeFindingId('R-71', undefined, `${reportType}:${fail.providerId}:block`),
        severity: 'block',
        tier: 'Tier 0',
        checkId: 'R-71',
        message: `${fail.providerLabel}: ${fail.message}`,
        detail: coverage.retryHint,
        reportType,
        providerSource: fail.providerId,
        action: coverage.retryHint,
      });
    }
    for (const fail of coverage.warnings) {
      findings.push({
        id: makeFindingId('R-71', undefined, `${reportType}:${fail.providerId}:warn`),
        severity: 'warn',
        tier: 'Tier 0',
        checkId: 'R-71',
        message: `${fail.providerLabel}: ${fail.message}`,
        detail: coverage.retryHint,
        reportType,
        providerSource: fail.providerId,
        action: coverage.retryHint,
      });
    }
  }

  const NO_CO_DETECTED_FIELDS: ReadonlyMap<string, readonly string[]> = new Map();
  for (const { report, sections, result } of sectionDepthResults) {
    const omittedIds = new Set((report.qaFindings?.sectionOmissions ?? []).map((o) => o.sectionId));
    const coDetected = coDetectedStubFieldsByReport.get(report.id) ?? NO_CO_DETECTED_FIELDS;
    for (const fail of result.failures) {
      const { label, href } = defaultActionForCheck('R-73', 'Tier 2', {
        siteId: site.id,
        reportId: report.id,
      });
      // An explicitly-omitted section cannot be expanded by regeneration — the
      // honest next step is adding evidence, so route to the Documents tab.
      const omitted = omittedIds.has(fail.sectionId);
      findings.push({
        // Keyed on the gate's own section ids only: folding the co-detected
        // names in here would re-key every existing chip and orphan its
        // dismissal.
        id: makeFindingId('R-73', report.id, `${fail.sectionId}:${fail.kind}`),
        severity: fail.severity,
        tier: 'Tier 2',
        checkId: 'R-73',
        // `report.reportType` scopes the alias resolution the fold's guard
        // runs in. Only the PATH-TABLE route needs it: `appendix` reaches
        // `sentimentAppendix` through SECTION_FIELD_CANDIDATES, which is
        // type-independent, but `waterRiskMatrix` reaches the `waterAnalysis`
        // object the auditor names only via SECTION_FIELD_PATHS['water-resource'].
        // Drop the argument and that section reads as one R-73 never named, so
        // the clause restates what R-73 already named.
        message: stubRepeatDetailWithCoDetectedFields(
          fail,
          sections,
          coDetected,
          report.reportType as ReportTypeId | undefined,
        ),
        reportId: report.id,
        reportType: report.reportType,
        action: omitted ? 'Add sources — section is unevidenced' : label,
        ...(omitted
          ? { actionHref: `/sites/${site.id}?tab=documents` }
          : href
            ? { actionHref: href }
            : {}),
      });
    }
  }

  const densityThresholdCfg = await loadCitationDensityThreshold(site.companyId);
  for (const report of reports) {
    const decision = evaluateCitationDensityGate(report, {
      threshold: densityThresholdCfg.effectiveThreshold,
      auditOverrides: site.auditOverrides,
      reportTypeId: report.reportType as ReportTypeId,
    });
    if (!decision.snapshot.belowThreshold) continue;
    const overridden = decision.pass && decision.snapshot.belowThreshold;
    findings.push({
      id: makeFindingId('R-3', report.id, `density:${decision.snapshot.density.toFixed(3)}`),
      severity: overridden ? 'info' : 'block',
      tier: 'Tier 2',
      checkId: 'R-3',
      message:
        `Citation density ${(decision.snapshot.density * 100).toFixed(0)}% `
        + `below ${(decision.snapshot.threshold * 100).toFixed(0)}% floor `
        + `(${decision.snapshot.citedClaims}/${decision.snapshot.totalClaims} claims cited)`,
      detail: overridden
        ? 'Admin override recorded — gate waived for dispatch.'
        : `${decision.snapshot.uncitedClaims.length} uncited claim(s) block publish without override.`,
      reportId: report.id,
      reportType: report.reportType,
      action: overridden ? 'Override on file' : 'Regenerate with citation focus or override with rationale',
    });
  }

  const qualitativeClaimByReport: Record<string, Awaited<ReturnType<typeof auditQualitativeClaims>>> = {};
  if (AUDIT_CONFIG.ENABLE_QUALITATIVE_CLAIM_AUDIT) {
    for (const report of reports) {
      const qaResult = await auditQualitativeClaims(report, sed, {
        maxClaims: AUDIT_CONFIG.QUALITATIVE_CLAIM_AUDIT_MAX,
        siteId,
        reportId: report.id,
        reportType: report.reportType,
      });
      qualitativeClaimByReport[report.id] = qaResult;
      for (const f of qaResult.findings) {
        findings.push({
          id: makeFindingId('R-8', report.id, `${f.claimId}:${f.verdict}`),
          severity: f.severity,
          tier: 'Tier 2',
          checkId: 'R-8',
          message: f.message,
          detail: f.rationale,
          reportId: report.id,
          reportType: report.reportType,
          action: f.verdict === 'CONTRADICTED'
            ? 'Revise claim or replace with SED-backed characterization'
            : 'Add supporting evidence or soften qualitative language',
        });
      }
    }
  }

  const sectionCoherenceByReport: Record<string, { reportType?: string; findings: import('./sectionCoherenceAuditor').CoherenceFinding[] }> = {};
  if (AUDIT_CONFIG.ENABLE_SECTION_COHERENCE_AUDIT) {
    for (const report of reports) {
      const { findings: coherenceFindings } = await auditSectionCoherence(report, {
        maxPairs: AUDIT_CONFIG.SECTION_COHERENCE_MAX_PAIRS,
        siteId,
        reportId: report.id,
      });
      if (coherenceFindings.length > 0) {
        sectionCoherenceByReport[report.id] = {
          reportType: report.reportType,
          findings: coherenceFindings,
        };
      }
      for (const f of coherenceFindings) {
        findings.push({
          id: makeFindingId('R-9', report.id, `${f.pairId}:${f.severity}`),
          severity: f.severity,
          tier: 'Tier 2',
          checkId: 'R-9',
          message: f.message,
          detail: f.rationale,
          reportId: report.id,
          reportType: report.reportType,
          action: 'Align section narratives or regenerate affected sections',
        });
      }
    }
  }

  const citationSupportByReport: Record<string, Awaited<ReturnType<typeof verifyCitationSupport>>> = {};
  if (AUDIT_CONFIG.ENABLE_CITATION_SUPPORT_VERIFICATION) {
    for (const report of reports) {
      const supportResult = await verifyCitationSupport(report, sed, {
        maxLlmCalls: AUDIT_CONFIG.CITATION_SUPPORT_MAX_LLM_CALLS,
        siteId,
        reportId: report.id,
        reportType: report.reportType,
        enabled: true,
      });
      citationSupportByReport[report.id] = supportResult;
      for (const f of supportResult.findings) {
        findings.push({
          id: makeFindingId('R-15', report.id, `${f.pairId}:${f.verdict}`),
          severity: f.severity,
          tier: 'Tier 2',
          checkId: 'R-15',
          message: f.fixHint,
          detail: f.rationale,
          reportId: report.id,
          reportType: report.reportType,
          action: 'Regenerate with fix hints or revise citation / claim text',
        });
      }
    }
  }

  let deduped = dedupeUserAuditFindings(findings);
  
  const activeAuditOverrides = resolveActiveAuditOverrides(site.auditOverrides);
  if (activeAuditOverrides.length > 0) {
    const validateOverrides = activeAuditOverrides.filter(
      (o) => ('type' in o && o.type === 'validate') || stripOverrideVersionSuffix(o.id).startsWith('validate:'),
    );
    const validatedIds = new Map(
      validateOverrides.map((o) => [stripOverrideVersionSuffix(o.logicalKey ?? o.id).replace('validate:', ''), o.id]),
    );
    // Validations also match on the regeneration-stable key (no report id) —
    // exact-id overrides silently expired the moment the report regenerated,
    // resurfacing every accepted finding as unresolved.
    const validatedStableKeys = new Map(
      validateOverrides
        .map((o) => ('stableKey' in o && o.stableKey ? ([o.stableKey, o.id] as const) : undefined))
        .filter((e): e is readonly [string, string] => Boolean(e)),
    );
    if (validatedIds.size > 0 || validatedStableKeys.size > 0) {
      // ANNOTATE, don't delete: a validated finding must stay visible (in a
      // settled group) and undoable. Dropping it made Validate an invisible,
      // permanent suppression — the chip could never render after a re-run.
      deduped = deduped.map((f) => {
        const overrideId =
          validatedIds.get(f.id) ??
          validatedStableKeys.get(makeStableFindingKey(f.checkId, f.reportType, f.message));
        return overrideId ? { ...f, validated: true, validatedByOverrideId: overrideId } : f;
      });
    }

    // Phase-3 dismiss/snooze — same annotate-don't-delete contract as
    // validate. An expired `until` (snooze) is skipped entirely, so the
    // finding RESURFACES as active on the next run.
    const now = Date.now();
    const dismissOverrides = activeAuditOverrides.filter(
      (o): o is Extract<typeof o, { type: 'dismiss' }> =>
        'type' in o && o.type === 'dismiss' &&
        (!o.until || new Date(o.until).getTime() > now),
    );
    if (dismissOverrides.length > 0) {
      const byId = new Map(dismissOverrides.map((o) => [o.findingId, o.id]));
      const byStable = new Map(
        dismissOverrides.flatMap((o) => (o.stableKey ? [[o.stableKey, o.id] as const] : [])),
      );
      deduped = deduped.map((f) => {
        if (f.validated) return f; // validate wins over dismiss
        const overrideId =
          byId.get(f.id) ?? byStable.get(makeStableFindingKey(f.checkId, f.reportType, f.message));
        return overrideId ? { ...f, dismissed: true, dismissedByOverrideId: overrideId } : f;
      });
    }
  }

  const lastRunAt = new Date().toISOString();

  return {
    siteId,
    lastRunAt,
    findings: deduped,
    severityCounts: aggregateSeverityCounts(deduped),
    durationMs: Date.now() - started,
    reportIds,
    intentSedReconciliation,
    precedenceByReport,
    canonicalReconciliation,
    qualitativeClaimByReport: Object.keys(qualitativeClaimByReport).length > 0
      ? qualitativeClaimByReport
      : undefined,
    sectionCoherenceByReport: Object.keys(sectionCoherenceByReport).length > 0
      ? sectionCoherenceByReport
      : undefined,
    citationSupportByReport: Object.keys(citationSupportByReport).length > 0
      ? citationSupportByReport
      : undefined,
  };
}
